import Fuse from 'fuse.js';
import { useCallback, useMemo } from 'react';
import { getAsyncStatus, getAsyncStatusFromRtkQueryStates, } from 'utils/apis/axios';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
/**
 * A hook that fetches non-paginated categories of items as well as paginated
 * items of the same type. These are then concatenated in a continuous
 * categorized sequence that appears paginated.
 */
export const useCategorizedList = ({ apis: { categorized: { result: categorizedResult, searchOptions: fuseOptions, changeArgItemPredicate, }, paginated: paginatedApi, }, identityFn, }) => {
    var _a, _b;
    const { items: paginatedItems, asyncStatus: paginatedItemsAsyncStatus, arg: paginatedItemsArg, search: paginatedItemsSearch, changeArg: paginatedItemsChangeArg, refetchItems: paginatedItemsRefetchItems, hasNextPage, isPaginating, nextPage, } = usePaginatedEndpoint(Object.assign(Object.assign({}, paginatedApi), { infinite: true }));
    const categories = useMemo(() => {
        var _a, _b;
        const categories = (_b = (_a = categorizedResult.data) === null || _a === void 0 ? void 0 : _a.data.categories) !== null && _b !== void 0 ? _b : [];
        if (!isPaginating &&
            (paginatedItemsAsyncStatus === 'pending' || categorizedResult.isFetching)) {
            return new Array({
                id: 'paginated',
                items: [],
            });
        }
        return new Array(...categories
            .map(({ id, items }) => {
            const fuse = new Fuse(items, fuseOptions);
            if (paginatedItemsArg === null || paginatedItemsArg === void 0 ? void 0 : paginatedItemsArg.searchTerm) {
                items = fuse
                    .search(paginatedItemsArg.searchTerm)
                    .map(({ item }) => item);
            }
            if (paginatedItemsArg && changeArgItemPredicate) {
                items = items.filter((item) => changeArgItemPredicate(item, paginatedItemsArg));
            }
            return { id, items };
        })
            .filter((category) => category.items.length), { id: 'paginated', items: paginatedItems });
        // Recompute only when the true dependencies changes as it would otherwise
        // execute too often due when the hook options are passed in anonymously.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        (_a = categorizedResult.data) === null || _a === void 0 ? void 0 : _a.data.categories,
        categorizedResult.isFetching,
        isPaginating,
        paginatedItems,
        paginatedItemsArg,
        paginatedItemsAsyncStatus,
    ]);
    const asyncStatus = useMemo(() => getAsyncStatus([
        paginatedItemsAsyncStatus,
        getAsyncStatusFromRtkQueryStates(categorizedResult),
    ]), [categorizedResult, paginatedItemsAsyncStatus]);
    const favorites = useMemo(() => {
        var _a, _b;
        return new Set(((_b = (_a = categorizedResult.data) === null || _a === void 0 ? void 0 : _a.data.categories) !== null && _b !== void 0 ? _b : [])
            .filter((category) => category.id === 'favorite')
            .flatMap((category) => category.items)
            .map(identityFn));
    }, [(_b = categorizedResult.data) === null || _b === void 0 ? void 0 : _b.data.categories, identityFn]);
    const refetch = useCallback(() => {
        categorizedResult.refetch();
        paginatedItemsRefetchItems();
    }, [categorizedResult, paginatedItemsRefetchItems]);
    return {
        categories,
        asyncStatus,
        hasNextPage,
        favorites,
        arg: paginatedItemsArg,
        changeArg: paginatedItemsChangeArg,
        search: paginatedItemsSearch,
        refetch,
        nextPage,
    };
};
